// M08 二次函數與二次方程 - 主聚合文件
// Quadratic Functions and Quadratic Equations - Main Aggregation File
// 系統化設計，準備後續 Supabase 數據遷移

import {
  M08_TOPICS_01_TO_10,
  M08_PROBLEMS_01_TO_10
} from './M08_Topics_01_to_10.js';

import {
  M08_TOPICS_11_TO_20,
  M08_PROBLEMS_11_TO_20
} from './M08_Topics_11_to_20.js';

import {
  M08_TOPICS_21_TO_31,
  M08_PROBLEMS_21_TO_31
} from './M08_Topics_21_to_31.js';

// M08 模組資訊
export const M08_MODULE_INFO = {
  id: 'M08',
  name: '二次函數與二次方程',
  description: '全面掌握二次函數圖像性質、二次方程求解方法與實際應用',
  category: 'advanced',
  difficulty: 'intermediate',
  estimatedTime: '18-25小時',
  totalQuestions: 155,
  totalTopics: 31,
  questionsPerTopic: 5,
  partsPerQuestion: 3,
  difficultyDistribution: {
    basic: 50,
    intermediate: 75,
    advanced: 30
  },
  keyFeatures: [
    '多種求解方法（因式分解、配方法、公式法）',
    '函數圖像分析與變換',
    '極值問題與優化應用',
    '實際問題建模',
    '參數討論與分類',
    '韋達定理應用'
  ],
  learningObjectives: [
    '熟練掌握二次方程的各種求解方法',
    '理解二次函數的圖像性質和變換規律',
    '能夠建立數學模型解決實際優化問題',
    '掌握判別式在根的性質分析中的應用',
    '運用韋達定理處理根與係數的關係',
    '培養數形結合的數學思維'
  ]
};

// 聚合所有主題
export const M08_TOPICS = [
  ...M08_TOPICS_01_TO_10,
  ...M08_TOPICS_11_TO_20,
  ...M08_TOPICS_21_TO_31
];

// 聚合所有問題數據
export const M08_PROBLEMS_DATA = [
  ...M08_PROBLEMS_01_TO_10,
  ...M08_PROBLEMS_11_TO_20,
  ...M08_PROBLEMS_21_TO_31
];

// 輔助函數：獲取所有問題
export function getAllM08Problems() {
  return M08_PROBLEMS_DATA;
}

// 輔助函數：根據主題ID獲取問題
export function getProblemsByTopicId(topicId) {
  return M08_PROBLEMS_DATA.filter(problem => problem.topicId === topicId);
}

// 輔助函數：根據難度獲取問題
export function getProblemsByDifficulty(difficulty) {
  return M08_PROBLEMS_DATA.filter(problem => problem.difficulty === difficulty);
}

// 輔助函數：獲取主題資訊
export function getTopicById(topicId) {
  return M08_TOPICS.find(topic => topic.id === topicId);
}

// 輔助函數：根據類別獲取主題
export function getTopicsByCategory(category) {
  return M08_TOPICS.filter(topic => topic.category === category);
}

// 輔助函數：獲取問題統計
export function getM08Statistics() {
  const totalProblems = M08_PROBLEMS_DATA.length;
  const totalTopics = M08_TOPICS.length;
  
  const difficultyCount = {
    basic: M08_PROBLEMS_DATA.filter(p => p.difficulty === 'basic').length,
    intermediate: M08_PROBLEMS_DATA.filter(p => p.difficulty === 'intermediate').length,
    advanced: M08_PROBLEMS_DATA.filter(p => p.difficulty === 'advanced').length
  };

  const categoryCount = {};
  M08_TOPICS.forEach(topic => {
    categoryCount[topic.category] = (categoryCount[topic.category] || 0) + 1;
  });

  return {
    totalProblems,
    totalTopics,
    difficultyCount,
    categoryCount,
    averageProblemsPerTopic: (totalProblems / totalTopics).toFixed(1)
  };
}

// 輔助函數：驗證數據完整性
export function validateM08Data() {
  const validation = {
    isValid: true,
    errors: [],
    warnings: []
  };

  // 檢查問題數量
  const expectedProblems = M08_MODULE_INFO.totalQuestions;
  const actualProblems = M08_PROBLEMS_DATA.length;
  
  if (actualProblems !== expectedProblems) {
    validation.errors.push(`問題數量不匹配：預期 ${expectedProblems}，實際 ${actualProblems}`);
    validation.isValid = false;
  }

  // 檢查主題數量
  const expectedTopics = M08_MODULE_INFO.totalTopics;
  const actualTopics = M08_TOPICS.length;
  
  if (actualTopics !== expectedTopics) {
    validation.errors.push(`主題數量不匹配：預期 ${expectedTopics}，實際 ${actualTopics}`);
    validation.isValid = false;
  }

  // 檢查每個主題是否都有對應的問題
  M08_TOPICS.forEach(topic => {
    const topicProblems = getProblemsByTopicId(topic.id);
    if (topicProblems.length === 0) {
      validation.warnings.push(`主題 ${topic.id} 沒有對應的問題`);
    } else if (topicProblems.length !== M08_MODULE_INFO.questionsPerTopic) {
      validation.warnings.push(`主題 ${topic.id} 的問題數量不正確：${topicProblems.length}`);
    }
  });

  // 檢查問題結構
  M08_PROBLEMS_DATA.forEach(problem => {
    if (!problem.id || !problem.topicId || !problem.type || !problem.difficulty) {
      validation.errors.push(`問題 ${problem.id || '未知'} 缺少必要字段`);
      validation.isValid = false;
    }

    if (problem.type === 'multi_part' && !problem.parts) {
      validation.errors.push(`多部分問題 ${problem.id} 缺少 parts 字段`);
      validation.isValid = false;
    }

    if (problem.parts) {
      const partKeys = Object.keys(problem.parts);
      if (!partKeys.includes('a') || !partKeys.includes('b') || !partKeys.includes('c')) {
        validation.warnings.push(`問題 ${problem.id} 的部分不完整`);
      }
    }
  });

  return validation;
}

// 輔助函數：為 Supabase 遷移準備數據
export function prepareForSupabaseMigration() {
  const migration = {
    module: M08_MODULE_INFO,
    topics: M08_TOPICS.map(topic => ({
      ...topic,
      moduleId: M08_MODULE_INFO.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    })),
    problems: M08_PROBLEMS_DATA.map(problem => ({
      ...problem,
      moduleId: M08_MODULE_INFO.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // 將 parts 轉換為 JSON 字符串以便存儲
      partsJson: problem.parts ? JSON.stringify(problem.parts) : null
    })),
    statistics: getM08Statistics(),
    validation: validateM08Data()
  };

  return migration;
}

// 輔助函數：搜索功能
export function searchM08Content(query) {
  const results = {
    topics: [],
    problems: []
  };

  const lowerQuery = query.toLowerCase();

  // 搜索主題
  results.topics = M08_TOPICS.filter(topic => 
    topic.name.toLowerCase().includes(lowerQuery) ||
    topic.description.toLowerCase().includes(lowerQuery) ||
    topic.category.toLowerCase().includes(lowerQuery)
  );

  // 搜索問題
  results.problems = M08_PROBLEMS_DATA.filter(problem => 
    problem.question?.toLowerCase().includes(lowerQuery) ||
    problem.topicId.toLowerCase().includes(lowerQuery) ||
    (problem.parts && Object.values(problem.parts).some(part => 
      part.question?.toLowerCase().includes(lowerQuery) ||
      part.hint?.toLowerCase().includes(lowerQuery)
    ))
  );

  return results;
}

// 輔助函數：獲取學習路徑建議
export function getM08LearningPath() {
  return {
    beginner: [
      'M08_08', // 因式分解
      'M08_02', // 二次方程求根
      'M08_03', // 二次方程求解
      'M08_18', // 配方法
      'M08_04', // 公式法
    ],
    intermediate: [
      'M08_09', // 判別式
      'M08_16', // 根與係數
      'M08_13', // 重根條件
      'M08_30', // 頂點判斷
      'M08_20', // 頂點公式
      'M08_25', // 對稱性分析
    ],
    advanced: [
      'M08_01', // 二次不等式
      'M08_21', // 極值分析
      'M08_22', // 極值應用
      'M08_11', // 建模
      'M08_27', // 應用問題解析
      'M08_29', // 變數代換
    ],
    applications: [
      'M08_11', // 建模
      'M08_22', // 極值應用
      'M08_27', // 應用問題解析
      'M08_28', // 聯立方程
    ]
  };
}

// 默認導出
export default {
  moduleInfo: M08_MODULE_INFO,
  topics: M08_TOPICS,
  problems: M08_PROBLEMS_DATA,
  getAllProblems: getAllM08Problems,
  getProblemsByTopicId,
  getProblemsByDifficulty,
  getTopicById,
  getTopicsByCategory,
  getStatistics: getM08Statistics,
  validateData: validateM08Data,
  prepareForSupabaseMigration,
  searchContent: searchM08Content,
  getLearningPath: getM08LearningPath
}; 